import { type FC, useEffect, useRef, useState } from "react";
import { type Draft, DraftStatus, type FormFieldDef } from "../types";
import { mmToPt } from "../utils";
import { Box, Button, Fade } from "@mui/material";

export const FieldConflictDialog: FC<{
    field: FormFieldDef;
    draft: Draft;
    scale: number;
    pagesize: { w: number; h: number };
    resolveConflict: (key: string, value: string) => void;
}> = ({ field, draft, pagesize, scale, resolveConflict }) => {
    // refs and state for draggable floating dialog
    const containerRef = useRef<HTMLDivElement | null>(null);
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const draggingRef = useRef<{ active: boolean; startX: number; startY: number; origX: number; origY: number }>({ active: false, startX: 0, startY: 0, origX: 0, origY: 0 });
    const userMovedRef = useRef<boolean>(false);
    const [userMoved, setUserMoved] = useState<boolean>(false);
    const [connectorTick, setConnectorTick] = useState<number>(0);

    const [dialogPos, setDialogPos] = useState<{ x: number; y: number } | null>(null);

    // compute initial position near the field when conflict opens
    useEffect(() => {
        if (draft.status !== DraftStatus.Conflict) return;

        // try to compute a reasonable initial screen position based on container location
        const containerRect = containerRef.current?.getBoundingClientRect();
        const dialogRect = dialogRef.current?.getBoundingClientRect();

        // field-relative coords within container
        const relX = mmToPt(field.rect.x) * scale;
        const relY = mmToPt(field.rect.y) * scale;

        // if we have container rect, place dialog at container left+relX, container top+relY - dialog height
        let x = relX;
        let y = relY;
        if (containerRect) {
            // containerRect is in viewport coordinates already. Don't add relX/relY again.
            x = containerRect.left + 1;
            // position the dialog above the field by default
            y = containerRect.top - (dialogRect?.height ?? (mmToPt(pagesize.h ?? 600) * 0.15));
        } else {
            // fallback: use viewport position based on rel coords (best-effort)
            x = relX + 10;
            y = relY - (dialogRect?.height ?? 100);
        }

        // constrain inside viewport
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const dw = dialogRect?.width ?? Math.min(vw * 0.6, mmToPt(pagesize.w ?? 800) * 0.5);
        const dh = dialogRect?.height ?? Math.min(vh * 0.5, mmToPt(pagesize.h ?? 600) * 0.3);
        x = Math.max(8, Math.min(x, vw - dw - 8));
        y = Math.max(8, Math.min(y, vh - dh - 8));

        setDialogPos({ x, y });
        // reset user-moved flag when a new conflict opens
        userMovedRef.current = false;
        setUserMoved(false);
    }, [draft.status, field.rect.x, field.rect.y, scale, pagesize.w, pagesize.h, field.key, draft.remote]);

    // clear userMoved when dialog closes
    useEffect(() => {
        if (draft.status !== DraftStatus.Conflict) {
            userMovedRef.current = false;
            setUserMoved(false);
        }
    }, [draft.status]);

    // follow scroll / resize so dialog stays near the field unless the user manually dragged it
    useEffect(() => {
        if (draft.status !== DraftStatus.Conflict) return;
        const updatePos = () => {
            // always trigger connector re-render
            try { setConnectorTick(t => t + 1); } catch { }
            if (userMovedRef.current) return; // if user moved, don't change dialogPos
            const containerRect = containerRef.current?.getBoundingClientRect();
            const dialogRect = dialogRef.current?.getBoundingClientRect();
            let x = 10;
            let y = 10;
            if (containerRect) {
                x = containerRect.left + 1;
                y = containerRect.top - (dialogRect?.height ?? (mmToPt(pagesize.h ?? 600) * 0.15));
            }
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const dw = dialogRect?.width ?? Math.min(vw * 0.6, mmToPt(pagesize.w ?? 800) * 0.5);
            const dh = dialogRect?.height ?? Math.min(vh * 0.5, mmToPt(pagesize.h ?? 600) * 0.3);
            x = Math.max(8, Math.min(x, vw - dw - 8));
            y = Math.max(8, Math.min(y, vh - dh - 8));
            setDialogPos({ x, y });
        };

        // helper: find nearest scrollable ancestor
        const findScrollParent = (el: HTMLElement | null): EventTarget => {
            let cur = el?.parentElement;
            while (cur) {
                const style = window.getComputedStyle(cur);
                const overflowY = style.overflowY;
                const overflowX = style.overflowX;
                if (/(auto|scroll|overlay)/.test(overflowY) || /(auto|scroll|overlay)/.test(overflowX)) {
                    return cur;
                }
                cur = cur.parentElement;
            }
            return window;
        };

        const scrollParent = findScrollParent(containerRef.current);

        // attach listeners
        (scrollParent as any).addEventListener('scroll', updatePos, { passive: true });
        window.addEventListener('scroll', updatePos, true); // capture in case
        window.addEventListener('resize', updatePos);

        // initial update
        updatePos();

        return () => {
            try { (scrollParent as any).removeEventListener('scroll', updatePos); } catch { }
            try { window.removeEventListener('scroll', updatePos, true); } catch { }
            try { window.removeEventListener('resize', updatePos); } catch { }
        };
    }, [draft.status, pagesize.h, pagesize.w]);

    const startDrag = (clientX: number, clientY: number) => {
        draggingRef.current.active = true;
        draggingRef.current.startX = clientX;
        draggingRef.current.startY = clientY;
        draggingRef.current.origX = dialogPos?.x ?? 0;
        draggingRef.current.origY = dialogPos?.y ?? 0;
        // mark as user moved so auto-follow doesn't override and show connector
        userMovedRef.current = true;
        setUserMoved(true);

        const onMove = (e: MouseEvent) => {
            if (!draggingRef.current.active) return;
            e.preventDefault();
            const clientX = e.clientX;
            const clientY = e.clientY;
            const dx = clientX - draggingRef.current.startX;
            const dy = clientY - draggingRef.current.startY;
            let nx = draggingRef.current.origX + dx;
            let ny = draggingRef.current.origY + dy;

            // constrain to viewport
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const rect = dialogRef.current?.getBoundingClientRect();
            const w = rect?.width ?? 200;
            const h = rect?.height ?? 120;
            nx = Math.max(8, Math.min(nx, vw - w - 8));
            ny = Math.max(8, Math.min(ny, vh - h - 8));

            setDialogPos({ x: nx, y: ny });
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!draggingRef.current.active) return;
            const t = e.touches[0];
            if (!t) return;
            const clientX = t.clientX;
            const clientY = t.clientY;
            const dx = clientX - draggingRef.current.startX;
            const dy = clientY - draggingRef.current.startY;
            let nx = draggingRef.current.origX + dx;
            let ny = draggingRef.current.origY + dy;

            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const rect = dialogRef.current?.getBoundingClientRect();
            const w = rect?.width ?? 200;
            const h = rect?.height ?? 120;
            nx = Math.max(8, Math.min(nx, vw - w - 8));
            ny = Math.max(8, Math.min(ny, vh - h - 8));

            setDialogPos({ x: nx, y: ny });
        };

        const onUp = () => {
            draggingRef.current.active = false;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onUp);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onTouchMove, { passive: false });
        window.addEventListener('touchend', onUp);
    };

    return (<Box
        ref={containerRef}
        sx={{
            position: "absolute",
            left: mmToPt(field.rect.x) * scale,
            top: mmToPt(field.rect.y) * scale,
            width: mmToPt(field.rect.w) * scale,
            height: mmToPt(field.rect.h) * scale,
            zIndex: 30,
            pointerEvents: "none", // 吹き出し以外はpointerイベントを通さない
        }}
    >
        {/* render as fixed so it can be dragged anywhere on screen */}
        {draft.status === DraftStatus.Conflict && userMoved && dialogPos && (
            <svg
                key={connectorTick}
                style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 9999 }}
            >
                {
                    (() => {
                        try {
                            const dlg = dialogRef.current?.getBoundingClientRect();
                            const fld = containerRef.current?.getBoundingClientRect();
                            if (!dlg || !fld) return null;
                            const x1 = dlg.left + dlg.width / 2;
                            const y1 = dlg.top + dlg.height / 2;
                            const x2 = fld.left + fld.width / 2;
                            const y2 = fld.top + fld.height / 2;
                            return (
                                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#1976d2" strokeWidth={2} strokeLinecap="round" strokeDasharray="4 4" />
                            );
                        } catch (e) {
                            return null;
                        }
                    })()
                }
            </svg>
        )}

        <Fade in={draft.status === DraftStatus.Conflict} style={{ zIndex: 10000 }}>
            <Box
                ref={dialogRef}
                onMouseDown={(e) => {
                    // start drag when clicking on dialog header area
                    // Only start drag for left button
                    if (e.button !== 0) return;
                    // ignore if not visible
                    if (draft.status !== DraftStatus.Conflict) return;
                    // start dragging
                    startDrag(e.clientX, e.clientY);
                    // attach global listeners via effect
                }}
                onTouchStart={(e) => {
                    const t = e.touches[0];
                    startDrag(t.clientX, t.clientY);
                }}
                sx={{
                    position: "fixed",
                    left: dialogPos ? `${dialogPos.x}px` : '0px',
                    top: dialogPos ? `${dialogPos.y}px` : '0px',
                    minWidth: `${mmToPt(pagesize.w ?? 800) * 0.25}px`,
                    maxWidth: `${mmToPt(pagesize.w ?? 800) * 0.8}px`,
                    minHeight: `${mmToPt(pagesize.h ?? 600) * 0.1}px`,
                    maxHeight: `${mmToPt(pagesize.h ?? 600) * 0.5}px`,
                    height: "auto",
                    width: "auto",
                    background: "#fff",
                    border: "1px solid #1976d2",
                    borderRadius: 2,
                    boxShadow: 3,
                    p: 1,
                    zIndex: 10000,
                    display: draft.status === DraftStatus.Conflict ? "block" : "none",
                    pointerEvents: "auto",
                    cursor: 'move',
                }}
            >
                <Box
                    sx={{
                        mb: 1,
                        whiteSpace: "pre-line",
                        fontSize: "14px",
                        color: "rgba(0,0,0,0.87)",
                        userSelect: 'none',
                    }}
                >
                    {`競合が発生しました: \n競合している値: ${draft.remote ?? ''}\nこの端末で入力中の値を採用しますか？`}
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => resolveConflict(field.key, draft.local ?? '')}
                    >
                        はい
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => resolveConflict(field.key, draft.remote ?? '')}
                    >
                        いいえ
                    </Button>
                </Box>
            </Box>
        </Fade>
    </Box>);
};